import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, Check, AlertCircle, CircleCheckBig, MoreHorizontal, Info } from "lucide-react";
import { Button } from "@headlessui/react";
import useAutomatizaciones from "../../hooks/useAutomatizaciones";
import { useNotifications } from "../../contexts/NotificationContext";
import clsx from "clsx";
import RichTextEditor from "./RichTextEditor";
import EditorHelp from "./EditorHelp";
import { obtenerFiltrosDisponibles, obtenerMensajeAyuda, obtenerFrecuenciaRecomendada, obtenerFiltrosUsuario } from "./filtrosContextuales";
import { useSelectedEvent } from "../../contexts/SelectedEventContext";

export default function ConfiguracionNueva({ campana, onVolver }) {
  const { actualizarAutomatizacion, obtenerAutomatizacionPorId, loading } = useAutomatizaciones();
  const { showSuccess, showError, showWarning } = useNotifications();
  const { eventos } = useSelectedEvent();

  // Estado para los 4 paneles de configuración
  const [panelActual, setPanelActual] = useState(1);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  // Panel 1: Disparador
  const [disparador, setDisparador] = useState(() => {
    if (campana?.disparador) {
      return {
        tipo: campana.disparador.tipo || "evento",
        evento_tipo: campana.disparador.evento_tipo || campana.disparador.evento || "",
        cron: campana.disparador.cron || "",
        dias_antes_vencimiento: campana.disparador.dias_antes_vencimiento || 3,
      };
    }
    return {
      tipo: "evento",
      evento_tipo: "",
      cron: "",
      dias_antes_vencimiento: 3,
    };
  });

  // Panel 2: Filtros de destinatarios (expandido con nuevos campos)
  const [filtros, setFiltros] = useState(() => {
    const defaultFiltros = {
      // Filtros básicos
      tiene_telefono: true,
      tiene_email: false,
      id_evento: "",
      
      // Filtros personales
      es_mayor_edad: null,
      rango_edad_min: null,
      rango_edad_max: null,
      
      // Filtros de boletos
      tiene_boletos: false,
      cantidad_boletos_min: null,
      
      // Filtros de deuda
      estado_deuda: [],
      monto_pendiente_min: null,
      monto_pendiente_max: null,
      dias_hasta_vencimiento: null,
      
      // Filtros de turnos
      turno_confirmado: null,
      tiene_turno_asignado: false,
      
      // Filtros de mesas
      mesa_seleccionada: null,
      tiene_restricciones_alimentarias: false,
      
      // Filtros de tutor
      tiene_tutor: false,
      
      // Filtros de contrato
      contrato_firmado: null,

      // Filtros de asistencia
      asistencia_confirmada: null,

      // Tipo de destinatario: "invitados" | "usuarios" | "ambos"
      destinatarios_tipo: "invitados",

      // Filtros de usuarios del sistema
      rol_usuario: [],
      activo_usuario: true,
      lugar_id_usuario: "",
    };
    
    if (campana?.filtros_destinatarios) {
      return {
        ...defaultFiltros,
        ...campana.filtros_destinatarios,
        id_evento: campana.filtros_destinatarios.id_evento || campana.id_evento || "",
      };
    }
    
    return defaultFiltros;
  });
  
  // Calcular filtros relevantes según el disparador
  const filtrosRelevantes = useMemo(() => {
    return obtenerFiltrosDisponibles(disparador.tipo, disparador.evento_tipo);
  }, [disparador.tipo, disparador.evento_tipo]);

  // Resetear todos los filtros cuando cambia el tipo de disparador/evento
  useEffect(() => {
    setFiltros(prev => ({
      // Solo conservar los básicos con sus valores actuales
      tiene_telefono: prev.tiene_telefono ?? true,
      tiene_email: false,
      id_evento: prev.id_evento ?? "",
      // Todo lo demás vuelve a defaults
      es_mayor_edad: null, rango_edad_min: null, rango_edad_max: null,
      tiene_boletos: false, cantidad_boletos_min: null,
      estado_deuda: [], monto_pendiente_min: null, monto_pendiente_max: null, dias_hasta_vencimiento: null,
      turno_confirmado: null, tiene_turno_asignado: false,
      mesa_seleccionada: null, tiene_restricciones_alimentarias: false,
      tiene_tutor: false, contrato_firmado: null,
      asistencia_confirmada: null,
      // Preservar destinatarios_tipo y filtros de usuario
      destinatarios_tipo: prev.destinatarios_tipo ?? "invitados",
      rol_usuario: [], activo_usuario: true, lugar_id_usuario: "",
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disparador.tipo, disparador.evento_tipo]);
  
  // Frecuencia recomendada según el disparador (para sugerencia en paso 4)
  const frecuenciaRecomendada = useMemo(() => {
    return obtenerFrecuenciaRecomendada(disparador.tipo, disparador.evento_tipo);
  }, [disparador.tipo, disparador.evento_tipo]);
  
  // Obtener mensaje de ayuda contextual
  const mensajeAyudaFiltros = useMemo(() => {
    return obtenerMensajeAyuda(disparador.tipo, disparador.evento_tipo);
  }, [disparador.tipo, disparador.evento_tipo]);

  // Panel 3: Acciones
  const [acciones, setAcciones] = useState(() => {
    console.log('🔧 Inicializando estado de acciones desde campana:', campana);
    console.log('⚡ campana.acciones:', campana?.acciones);
    
    if (campana?.acciones && campana.acciones.length > 0) {
      const accionesMapeadas = campana.acciones.map(accion => ({
        tipo: accion.tipo || "whatsapp",
        plantilla: accion.plantilla || "",
        mensaje: accion.mensaje || "",
        asunto: accion.asunto || "",
        activa: accion.activa !== undefined ? accion.activa : true,
      }));
      console.log('✅ Acciones mapeadas:', accionesMapeadas);
      return accionesMapeadas;
    }
    
    console.log('⚠️ No hay acciones en campana, usando default (WhatsApp)');
    return [
      {
        tipo: "whatsapp",
        plantilla: "",
        mensaje: "",
        activa: true,
      },
    ];
  });

  // Panel 4: Frecuencia
  const [frecuencia, setFrecuencia] = useState(() => {
    if (campana?.frecuencia) {
      return {
        tipo: campana.frecuencia.tipo || "unica",
        intervalo_horas: campana.frecuencia.intervalo_horas || 24,
        max_envios: campana.frecuencia.max_envios || 3,
      };
    }
    return {
      tipo: "unica",
      intervalo_horas: 24,
      max_envios: 3,
    };
  });

  // Log para debug - ver qué datos tiene campana
  useEffect(() => {
    if (campana) {
      console.log("📋 Campaña cargada:", campana.nombre);
      console.log("🎯 Disparador:", disparador);
      console.log("📊 Filtros:", filtros);
      console.log("⚡ Acciones:", acciones);
      console.log("⏰ Frecuencia:", frecuencia);
    }
  }, [campana, disparador, filtros, acciones, frecuencia]);

  // Función para verificar si cada paso está completado
  const verificarPasoCompletado = (pasoId) => {
    switch (pasoId) {
      case 1: // Disparador
        if (disparador.tipo === "evento") {
          return !!disparador.evento_tipo;
        }
        if (disparador.tipo === "programado") {
          return !!disparador.cron;
        }
        if (disparador.tipo === "fecha_relativa") {
          return disparador.dias_antes_vencimiento > 0;
        }
        return false;

      case 2: // Filtros
        // Los filtros son opcionales, siempre se considera completado
        // (tiene_telefono viene por defecto en true)
        return true;

      case 3: // Acciones
        const accionesActivas = acciones.filter(a => a.activa);
        if (accionesActivas.length === 0) return false;
        // Verificar que cada acción activa tenga configuración completa
        return accionesActivas.every(accion => {
          if (accion.tipo === "whatsapp") {
            return !!accion.mensaje;
          }
          if (accion.tipo === "email") {
            return !!accion.asunto && !!accion.plantilla;
          }
          if (accion.tipo === "signalr") {
            return !!accion.evento && !!accion.datos;
          }
          return false;
        });

      case 4: // Frecuencia
        if (frecuencia.tipo === "recurrente") {
          return frecuencia.intervalo_horas > 0 && frecuencia.max_envios > 0;
        }
        return true; // unica y continua siempre válidas

      default:
        return false;
    }
  };

  // Pasos visuales para el sidebar con estado dinámico
  const pasos = [
    { id: 1, titulo: "Disparador", descripcion: "Define cuándo se ejecuta", completado: verificarPasoCompletado(1) },
    { id: 2, titulo: "Filtros", descripcion: "Selecciona destinatarios", completado: verificarPasoCompletado(2) },
    { id: 3, titulo: "Acciones", descripcion: "Qué se ejecutará", completado: verificarPasoCompletado(3) },
    { id: 4, titulo: "Frecuencia", descripcion: "Con qué frecuencia", completado: verificarPasoCompletado(4) },
  ];

  // Validar configuración antes de guardar
  const validarConfiguracion = () => {
    const nuevosErrores = {};

    // Validar disparador
    if (disparador.tipo === "evento" && !disparador.evento_tipo) {
      nuevosErrores.disparador = "Debe seleccionar un tipo de evento";
    }
    if (disparador.tipo === "programado" && !disparador.cron) {
      nuevosErrores.disparador = "Debe especificar una expresión cron";
    }
    if (disparador.tipo === "fecha_relativa" && !disparador.dias_antes_vencimiento) {
      nuevosErrores.disparador = "Debe especificar días antes del vencimiento";
    }

    // Validar acciones
    const accionesActivas = acciones.filter(a => a.activa);
    if (accionesActivas.length === 0) {
      nuevosErrores.acciones = "Debe haber al menos una acción activa";
    }
    
    accionesActivas.forEach((accion, idx) => {
      if (accion.tipo === "whatsapp" && !accion.mensaje) {
        nuevosErrores[`accion_${idx}`] = "El mensaje de WhatsApp no puede estar vacío";
      }
      if (accion.tipo === "email" && (!accion.asunto || !accion.plantilla)) {
        nuevosErrores[`accion_${idx}`] = "El email debe tener asunto y plantilla";
      }
    });

    // Validar frecuencia
    if (frecuencia.tipo === "recurrente") {
      if (!frecuencia.intervalo_horas || frecuencia.intervalo_horas < 1) {
        nuevosErrores.frecuencia = "El intervalo debe ser al menos 1 hora";
      }
      if (!frecuencia.max_envios || frecuencia.max_envios < 1) {
        nuevosErrores.frecuencia = "El máximo de envíos debe ser al menos 1";
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Guardar configuración
  const handleGuardar = async () => {
    if (!validarConfiguracion()) {
      showWarning("Por favor corrige los errores antes de guardar");
      return;
    }

    setGuardando(true);
    try {
      console.log('💾 Guardando configuración...');
      console.log('⚡ Acciones a guardar:', acciones);
      
      const automatizacionActualizada = await actualizarAutomatizacion(campana.id, {
        disparador,
        filtros_destinatarios: filtros,
        acciones,
        frecuencia,
      });
      
      console.log('✅ Automatización guardada:', automatizacionActualizada);
      console.log('⚡ Acciones en automatización guardada:', automatizacionActualizada?.acciones);
      
      // 🆕 SOLUCIÓN: Recargar datos frescos del backend antes de volver
      console.log('🔄 Recargando datos actualizados del backend...');
      const automatizacionFresca = await obtenerAutomatizacionPorId(campana.id);
      console.log('✨ Automatización fresca del backend:', automatizacionFresca);
      console.log('⚡ Acciones en automatización fresca:', automatizacionFresca?.acciones);
      
      showSuccess("Configuración guardada exitosamente");
      onVolver();
    } catch (error) {
      console.error("Error al guardar:", error);
      showError("Error al guardar la configuración");
    } finally {
      setGuardando(false);
    }
  };

  // Activar/Desactivar automatización
  const handleToggleActivo = async () => {
    // Verificar que todos los pasos estén completados antes de activar
    const todosCompletados = pasos.every(p => p.completado);
    
    if (!campana.activa && !todosCompletados) {
      showWarning("Debes completar todos los pasos antes de activar la automatización");
      return;
    }

    setGuardando(true);
    try {
      await actualizarAutomatizacion(campana.id, {
        activa: !campana.activa,
        disparador,
        filtros_destinatarios: filtros,
        acciones,
        frecuencia,
      });
      
      showSuccess(`Automatización ${!campana.activa ? "activada" : "desactivada"} exitosamente`);
      onVolver();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      showError(error.message || "Error al cambiar el estado de la automatización");
    } finally {
      setGuardando(false);
    }
  };

  // Navegación entre paneles
  const siguientePanelnext = () => {
    if (panelActual < 4) {
      setPanelActual(panelActual + 1);
    }
  };

  const panelAnterior = () => {
    if (panelActual > 1) {
      setPanelActual(panelActual - 1);
    }
  };

  const irAPanel = (numero) => {
    setPanelActual(numero);
  };

  return (
    <div className="min-h-screen border rounded-3xl p-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Panel Izquierdo - Flujo de Pasos */}
          <div className="lg:col-span-2 bg-fondoVs dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm">
            {/* Header */}
            <div className="flex gap-3 items-center mb-6">
              <Button
                onClick={onVolver}
                className="flex items-center gap-2 text-white bg-casal px-2 rounded-lg py-1 hover:bg-casal/80 font-medium"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="bg-white dark:bg-[#2a2a2a] rounded-full px-5 py-1 text-casal font-semibold">
                Configuración: {campana?.nombre}
              </span>
            </div>

            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl font-semibold text-casal mb-2">
                Editar el flujo
              </h2>
              {/* Lista de Pasos */}
              <div className="space-y-4 w-full">
                {pasos.map((paso, index) => (
                  <div key={paso.id} className="relative">
                    {/* Línea conectora */}
                    <div>
                      {index < pasos.length - 1 && (
                        <div className="absolute left-[26px] top-14 w-1 h-5 bg-Acapulco"></div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex-1 bg-gray-50 dark:bg-[#2a2a2a] rounded-lg p-4 flex items-center justify-between cursor-pointer ${
                          panelActual === paso.id
                            ? "ring-2 ring-casal"
                            : ""
                        }`}
                        onClick={() => irAPanel(paso.id)}
                      >
                        <div className="flex items-center">
                          <CircleCheckBig
                            className={`h-6 w-6 mr-3 ${
                              paso.completado ? "text-casal" : "text-gray-400"
                            }`}
                          />
                          <span
                            className={
                              paso.completado
                                ? "font-semibold text-casal"
                                : "font-medium text-gray-700 dark:text-gray-300"
                            }
                          >
                            {paso.titulo}
                          </span>
                        </div>
                        <Button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          <MoreHorizontal className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Botones de acción */}
            <div className="justify-center flex mt-8 px-4 w-full">
              <div className="flex flex-col sm:flex-row gap-3 p-3 bg-white dark:bg-[#2a2a2a] rounded-2xl sm:rounded-full shadow-md w-full sm:w-auto">
                <Button 
                  onClick={handleGuardar}
                  disabled={guardando || loading}
                  className={clsx(
                    "bg-casal hover:bg-casal/90 text-white px-6 py-2.5 rounded-xl sm:rounded-3xl font-medium w-full sm:w-auto text-center",
                    (guardando || loading) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {guardando ? "Guardando..." : "Guardar cambios"}
                </Button>
                <Button 
                  onClick={handleToggleActivo}
                  disabled={guardando || loading}
                  className={clsx(
                    "flex items-center justify-center gap-2 rounded-xl sm:rounded-3xl px-6 py-2.5 w-full sm:w-auto text-center",
                    campana?.activa 
                      ? "bg-red-500 hover:bg-red-600 text-white" 
                      : "bg-green-500 hover:bg-green-600 text-white",
                    (guardando || loading) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {campana?.activa ? "Desactivar" : "Activar"}
                </Button>
              </div>
            </div>
          </div>

          {/* Panel Derecho - Configuración */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border-2">
              {/* Errores de validación */}
              {Object.keys(errores).length > 0 && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">Errores de validación:</span>
                  </div>
                  <ul className="mt-2 ml-7 list-disc text-sm text-red-700 dark:text-red-400">
                    {Object.values(errores).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Contenido de paneles */}
              <div className="min-h-[500px]">
                {panelActual === 1 && (
                  <PanelDisparador
                    disparador={disparador}
                    setDisparador={setDisparador}
                    setFrecuencia={setFrecuencia}
                    error={errores.disparador}
                  />
                )}
                {panelActual === 2 && (
                  <PanelFiltros
                    filtros={filtros}
                    setFiltros={setFiltros}
                    filtrosRelevantes={filtrosRelevantes}
                    mensajeAyuda={mensajeAyudaFiltros}
                    disparador={disparador}
                    eventos={eventos}
                  />
                )}
                {panelActual === 3 && (
                  <PanelAcciones
                    acciones={acciones}
                    setAcciones={setAcciones}
                    errores={errores}
                  />
                )}
                {panelActual === 4 && (
                  <PanelFrecuencia
                    frecuencia={frecuencia}
                    setFrecuencia={setFrecuencia}
                    disparador={disparador}
                    frecuenciaRecomendada={frecuenciaRecomendada}
                    error={errores.frecuencia}
                  />
                )}
              </div>

              {/* Navegación entre paneles */}
              <div className="flex justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={panelAnterior}
                  disabled={panelActual === 1}
                  className={clsx(
                    "px-6 py-2 rounded-full font-semibold transition-colors",
                    panelActual === 1
                      ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  )}
                >
                  ← Anterior
                </Button>
                <Button
                  onClick={siguientePanelnext}
                  disabled={panelActual === 4}
                  className={clsx(
                    "px-6 py-2 rounded-full font-semibold transition-colors",
                    panelActual === 4
                      ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                      : "bg-casal hover:bg-casal/90 text-white"
                  )}
                >
                  Siguiente →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Ayuda flotante del editor */}
      {(panelActual === 3) && <EditorHelp />}
    </div>
  );
}

// ========== PANEL 1: DISPARADOR ==========

// Atajos de plantillas comunes para el panel de disparador
const ATAJOS_PLANTILLA = [
  {
    emoji: "👋",
    label: "Bienvenida al registrarse",
    disparador: { tipo: "evento", evento_tipo: "registro_completo" },
    frecuencia: { tipo: "unica", max_envios: 1 },
  },
  {
    emoji: "💰",
    label: "Recordatorio de pago",
    disparador: { tipo: "fecha_relativa", dias_antes_vencimiento: 3 },
    frecuencia: { tipo: "recurrente", intervalo_horas: 48, max_envios: 3 },
  },
  {
    emoji: "✅",
    label: "Confirmar pago recibido",
    disparador: { tipo: "evento", evento_tipo: "pago_completado" },
    frecuencia: { tipo: "unica", max_envios: 1 },
  },
  {
    emoji: "⏰",
    label: "Recordatorio de turno",
    disparador: { tipo: "evento", evento_tipo: "turno_asignado" },
    frecuencia: { tipo: "unica", max_envios: 1 },
  },
  {
    emoji: "🪑",
    label: "Confirmar mesa seleccionada",
    disparador: { tipo: "evento", evento_tipo: "mesa_seleccionada" },
    frecuencia: { tipo: "unica", max_envios: 1 },
  },
];

// Opciones amigables de horario para "Programado"
const HORARIOS_DISPONIBLES = [
  "07:00", "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00", "18:00",
  "19:00", "20:00", "21:00",
];

const OPCIONES_REPETICION = [
  { value: "diario", label: "Todos los días", cron: (h, m) => `0 ${m} ${h} * * *` },
  { value: "lunes-viernes", label: "Lunes a Viernes", cron: (h, m) => `0 ${m} ${h} * * 1-5` },
  { value: "fines-semana", label: "Sábado y Domingo", cron: (h, m) => `0 ${m} ${h} * * 0,6` },
  { value: "semanal-lunes", label: "Cada semana (lunes)", cron: (h, m) => `0 ${m} ${h} * * 1` },
  { value: "semanal-viernes", label: "Cada semana (viernes)", cron: (h, m) => `0 ${m} ${h} * * 5` },
];

function buildCron(hora, repeticion) {
  const [h, m] = hora.split(":").map(Number);
  const opcion = OPCIONES_REPETICION.find((o) => o.value === repeticion);
  return opcion ? opcion.cron(h, m) : `0 ${m} ${h} * * *`;
}

function PanelDisparador({ disparador, setDisparador, setFrecuencia, error }) {
  // Estado local para los controles amigables del modo "programado"
  const [horaEjecucion, setHoraEjecucion] = useState(() => {
    // Intentar parsear cron existente
    if (disparador.cron) {
      const parts = disparador.cron.split(" ");
      if (parts.length >= 3) {
        const h = parts[2].padStart(2, "0");
        const m = (parts[1] || "0").padStart(2, "0");
        return `${h}:${m}`;
      }
    }
    return "09:00";
  });
  const [repeticion, setRepeticion] = useState("diario");

  // Reconstruir cron cuando cambia hora o repetición
  const actualizarCron = (nuevaHora, nuevaRepeticion) => {
    const cron = buildCron(nuevaHora, nuevaRepeticion);
    setDisparador((d) => ({ ...d, cron }));
  };

  const handleHora = (v) => {
    setHoraEjecucion(v);
    actualizarCron(v, repeticion);
  };
  const handleRepeticion = (v) => {
    setRepeticion(v);
    actualizarCron(horaEjecucion, v);
  };

  // Aplicar un atajo de plantilla
  const aplicarAtajo = (atajo) => {
    setDisparador((d) => ({ ...d, ...atajo.disparador }));
    setFrecuencia((f) => ({ ...f, ...atajo.frecuencia }));
  };

  return (
    <div>
      <h3 className="text-xl font-bold text-casal mb-4">
        1. Configurar Disparador
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        Define cuándo se debe ejecutar esta automatización
      </p>

      {/* Atajos de plantillas */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-[#2a2a2a] rounded-xl">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Casos de uso frecuentes
        </p>
        <div className="flex flex-wrap gap-2">
          {ATAJOS_PLANTILLA.map((atajo) => (
            <button
              key={atajo.label}
              type="button"
              onClick={() => aplicarAtajo(atajo)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1a1a1a] hover:border-casal hover:text-casal dark:hover:border-casal dark:hover:text-casal transition-colors"
            >
              <span>{atajo.emoji}</span>
              <span>{atajo.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tipo de disparador */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Tipo de disparador
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { value: "evento", label: "Cuando ocurre algo", desc: "Pago, registro, turno asignado…" },
            { value: "programado", label: "En horario fijo", desc: "A una hora específica cada día" },
            { value: "fecha_relativa", label: "Días antes de…", desc: "X días antes de un vencimiento" },
          ].map((tipo) => (
            <button
              key={tipo.value}
              type="button"
              onClick={() => setDisparador((d) => ({ ...d, tipo: tipo.value }))}
              className={clsx(
                "p-4 rounded-lg border-2 text-left transition-all",
                disparador.tipo === tipo.value
                  ? "border-casal bg-casal/10"
                  : "border-gray-300 dark:border-gray-600 hover:border-casal/50"
              )}
            >
              <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                {tipo.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {tipo.desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Configuración específica: Evento */}
      {disparador.tipo === "evento" && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            ¿Qué evento lo dispara?
          </label>
          <select
            value={disparador.evento_tipo}
            onChange={(e) =>
              setDisparador((d) => ({ ...d, evento_tipo: e.target.value }))
            }
            className="w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-sm sm:text-base"
          >
            <option value="">Selecciona un evento...</option>
            <optgroup label="── Registro del Graduado ──">
              <option value="registro_iniciado">Registro Iniciado (formulario enviado)</option>
              <option value="pendiente_firma_contrato">Pendiente de Firma (espera firmar)</option>
              <option value="registro_completo">Registro Completo (con deuda generada)</option>
              <option value="contrato_firmado">Contrato Firmado (acto de firma)</option>
              <option value="asistencia_confirmada">Asistencia Confirmada</option>
              <option value="invitado_actualizado">Datos Actualizados</option>
            </optgroup>
            <optgroup label="── Pagos / Deudas ──">
              <option value="pago_completado">Pago Completado</option>
              <option value="pago_parcial">Pago Parcial</option>
              <option value="deuda_vencida">Deuda Vencida</option>
            </optgroup>
            <optgroup label="── Turnos / Mesas ──">
              <option value="turno_asignado">Turno Asignado</option>
              <option value="turno_confirmado">Turno Confirmado</option>
              <option value="mesa_seleccionada">Mesa Seleccionada</option>
            </optgroup>
          </select>
          
          {disparador.evento_tipo && (
            <div className="mt-2 text-xs text-casal dark:text-Acapulco font-medium bg-casal/5 dark:bg-casal/20 p-2 rounded-lg border border-casal/10 flex items-center gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
              <span className="font-bold">Seleccionado:</span>
              <span>
                {disparador.evento_tipo === "registro_iniciado" && "Registro Iniciado (formulario enviado)"}
                {disparador.evento_tipo === "pendiente_firma_contrato" && "Pendiente de Firma (espera firmar)"}
                {disparador.evento_tipo === "registro_completo" && "Registro Completo (con deuda generada)"}
                {disparador.evento_tipo === "contrato_firmado" && "Contrato Firmado (acto de firma)"}
                {disparador.evento_tipo === "asistencia_confirmada" && "Asistencia Confirmada"}
                {disparador.evento_tipo === "invitado_actualizado" && "Datos Actualizados"}
                {disparador.evento_tipo === "pago_completado" && "Pago Completado"}
                {disparador.evento_tipo === "pago_parcial" && "Pago Parcial"}
                {disparador.evento_tipo === "deuda_vencida" && "Deuda Vencida"}
                {disparador.evento_tipo === "turno_asignado" && "Turno Asignado"}
                {disparador.evento_tipo === "turno_confirmado" && "Turno Confirmado"}
                {disparador.evento_tipo === "mesa_seleccionada" && "Mesa Seleccionada"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Configuración específica: Programado → UI amigable */}
      {disparador.tipo === "programado" && (
        <div className="mb-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Hora del día
            </label>
            <select
              value={horaEjecucion}
              onChange={(e) => handleHora(e.target.value)}
              className="w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
            >
              {HORARIOS_DISPONIBLES.map((h) => (
                <option key={h} value={h}>{h} hrs</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              ¿Con qué frecuencia?
            </label>
            <div className="grid grid-cols-1 gap-2">
              {OPCIONES_REPETICION.map((op) => (
                <label key={op.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="repeticion"
                    value={op.value}
                    checked={repeticion === op.value}
                    onChange={(e) => handleRepeticion(e.target.value)}
                    className="text-casal focus:ring-casal"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{op.label}</span>
                </label>
              ))}
            </div>
          </div>
          {disparador.cron && (
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
              Expresión generada: <code>{disparador.cron}</code>
            </p>
          )}
        </div>
      )}

      {/* Configuración específica: Fecha relativa */}
      {disparador.tipo === "fecha_relativa" && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            ¿Cuántos días antes del vencimiento?
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="1"
              max="30"
              value={disparador.dias_antes_vencimiento}
              onChange={(e) =>
                setDisparador((d) => ({
                  ...d,
                  dias_antes_vencimiento: parseInt(e.target.value) || 1,
                }))
              }
              className="w-28 p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-center text-lg font-semibold"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              días antes de que venza una deuda
            </span>
          </div>
          <div className="flex gap-2 mt-3">
            {[1, 3, 5, 7, 14].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() =>
                  setDisparador((prev) => ({ ...prev, dias_antes_vencimiento: d }))
                }
                className={clsx(
                  "px-3 py-1 text-sm rounded-full border transition-colors",
                  disparador.dias_antes_vencimiento === d
                    ? "bg-casal text-white border-casal"
                    : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-casal"
                )}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

// ========== COMPONENTE: CONSTRUCTOR VISUAL DE CRON ==========
const DAYS_OF_WEEK = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mié" },
  { value: 4, label: "Jue" },
  { value: 5, label: "Vie" },
  { value: 6, label: "Sáb" },
];

const TIPOS_CRON = [
  { id: "intervalo", label: "Cada cierto tiempo" },
  { id: "daily", label: "Diario" },
  { id: "weekly", label: "Semanal" },
  { id: "custom", label: "Expresión manual" },
];

function parseCronExistente(cronExpr) {
  if (!cronExpr) return { tipo: "intervalo", cada: 1, unidad: "horas", hour: 9, min: 0, day: 1 };
  const intervalMatch = cronExpr.match(/^0 \*\/(\d+) \* \* \* \*$/);
  if (intervalMatch) return { tipo: "intervalo", cada: parseInt(intervalMatch[1]), unidad: "minutos", hour: 9, min: 0, day: 1 };
  const intervalHourMatch = cronExpr.match(/^0 0 \*\/(\d+) \* \* \*$/);
  if (intervalHourMatch) return { tipo: "intervalo", cada: parseInt(intervalHourMatch[1]), unidad: "horas", hour: 9, min: 0, day: 1 };
  const daily = cronExpr.match(/^0 (\d+) (\d+) \* \* \*$/);
  if (daily) return { tipo: "daily", cada: 1, unidad: "horas", hour: parseInt(daily[2]), min: parseInt(daily[1]), day: 1 };
  const weekly = cronExpr.match(/^0 (\d+) (\d+) \* \* (\d)$/);
  if (weekly) return { tipo: "weekly", cada: 1, unidad: "horas", hour: parseInt(weekly[2]), min: parseInt(weekly[1]), day: parseInt(weekly[3]) };
  return { tipo: "custom", cada: 1, unidad: "horas", hour: 9, min: 0, day: 1 };
}

function generarDescripcion(tipo, cada, unidad, hour, min, day) {
  switch (tipo) {
    case "intervalo":
      if (unidad === "minutos") return `Se ejecuta cada ${cada} ${cada === 1 ? "minuto" : "minutos"}.`;
      return `Se ejecuta cada ${cada} ${cada === 1 ? "hora" : "horas"}.`;
    case "daily":
      return `Se ejecuta todos los días a las ${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}.`;
    case "weekly":
      const dias = ["domingos", "lunes", "martes", "miércoles", "jueves", "viernes", "sábados"];
      return `Se ejecuta los ${dias[day]} a las ${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}.`;
    default:
      return "";
  }
}

function generarCron(tipo, cada, unidad, hour, min, day) {
  switch (tipo) {
    case "intervalo":
      if (unidad === "minutos") return `0 */${cada} * * * *`;
      return `0 0 */${cada} * * *`;
    case "daily":
      return `0 ${String(min).padStart(2, "0")} ${String(hour).padStart(2, "0")} * * *`;
    case "weekly":
      return `0 ${String(min).padStart(2, "0")} ${String(hour).padStart(2, "0")} * * ${day}`;
    default:
      return "";
  }
}

function ProgramadoBuilder({ cron, onChange }) {
  const estado = parseCronExistente(cron);
  const [tipo, setTipo] = useState(estado.tipo);
  const [cada, setCada] = useState(estado.cada);
  const [unidad, setUnidad] = useState(estado.unidad);
  const [hour, setHour] = useState(estado.hour);
  const [min, setMin] = useState(estado.min);
  const [day, setDay] = useState(estado.day);
  const [raw, setRaw] = useState(estado.tipo === "custom" ? cron : "");

  const descripcion = tipo === "custom" ? "" : generarDescripcion(tipo, cada, unidad, hour, min, day);
  const cronGenerado = tipo === "custom" ? raw : generarCron(tipo, cada, unidad, hour, min, day);

  useEffect(() => {
    if (tipo !== "custom" && cronGenerado !== cron) {
      onChange(cronGenerado);
    }
  }, [cronGenerado, tipo]);

  return (
    <div className="mb-8">
      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-5">
        Frecuencia de ejecución
      </label>

      <div className="flex gap-3 mb-6 flex-wrap">
        {TIPOS_CRON.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTipo(t.id);
              if (t.id === "custom") setRaw(cron || "");
            }}
            className={clsx(
              "px-5 py-3 rounded-xl border-2 font-medium transition-all",
              tipo === t.id
                ? "border-casal bg-casal/10 text-casal shadow-sm"
                : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-casal/50"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tipo === "intervalo" && (
        <div className="space-y-5">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Cada
              </label>
              <input
                type="number"
                min={1}
                value={cada}
                onChange={(e) => {
                  const val = Math.max(1, parseInt(e.target.value) || 1);
                  setCada(val);
                }}
                className="w-24 p-4 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-base text-center"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Unidad
              </label>
              <select
                value={unidad}
                onChange={(e) => setUnidad(e.target.value)}
                className="p-4 pr-8 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-base"
              >
                <option value="minutos">Minutos</option>
                <option value="horas">Horas</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {tipo === "daily" && (
        <div className="flex items-start gap-6 flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              Hora
            </label>
            <select
              value={hour}
              onChange={(e) => setHour(parseInt(e.target.value))}
              className="p-4 pr-8 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-base min-w-[90px]"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              Minuto
            </label>
            <select
              value={min}
              onChange={(e) => setMin(parseInt(e.target.value))}
              className="p-4 pr-8 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-base min-w-[90px]"
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {tipo === "weekly" && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
              Día de la semana
            </label>
            <div className="flex gap-3 flex-wrap">
              {DAYS_OF_WEEK.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setDay(d.value)}
                  className={clsx(
                    "w-14 h-14 rounded-full text-base font-semibold transition-all",
                    day === d.value
                      ? "bg-casal text-white shadow-md"
                      : "bg-gray-100 dark:bg-[#2a2a2a] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 border-2 border-transparent hover:border-casal/50"
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-6 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Hora
              </label>
              <select
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value))}
                className="p-4 pr-8 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-base min-w-[90px]"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Minuto
              </label>
              <select
                value={min}
                onChange={(e) => setMin(parseInt(e.target.value))}
                className="p-4 pr-8 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 text-base min-w-[90px]"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {tipo === "custom" && (
        <div className="space-y-4">
          <input
            type="text"
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              onChange(e.target.value);
            }}
            placeholder="0 0 9 * * 1"
            className="w-full p-4 border-2 rounded-xl bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 font-mono text-base"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Formato: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">segundo minuto hora día mes día-semana</code>
          </p>
          <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <p className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
              Ejemplos:<br />
              <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded">0 0 10 * * *</code> — Diario a las 10:00 AM<br />
              <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded">0 0 9 * * 1</code> — Lunes a las 9:00 AM<br />
              <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded">0 */30 * * * *</code> — Cada 30 minutos
            </p>
          </div>
        </div>
      )}

      {tipo !== "custom" && (
        <div className="mt-6 p-5 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl">
          <p className="text-base text-green-800 dark:text-green-300 font-medium">
            {descripcion}
          </p>
          <p className="text-sm text-green-600 dark:text-green-400 mt-2 font-mono">
            Cron: {cronGenerado}
          </p>
        </div>
      )}
    </div>
  );
}

// ========== PANEL 2: FILTROS ==========
function PanelFiltros({ filtros, setFiltros, filtrosRelevantes, mensajeAyuda, disparador, eventos }) {
  // Función auxiliar para actualizar filtro
  const actualizarFiltro = (key, value) => {
    setFiltros({ ...filtros, [key]: value });
  };

  // Contar filtros activos
  const contarFiltrosActivos = () => {
    return Object.entries(filtros).filter(([key, value]) => {
      if (key === 'id_evento') return false;
      if (key === 'destinatarios_tipo') return false; // No cuenta como filtro
      if (key === 'activo_usuario') return false; // Es un default, no filtro activo
      if (value === null || value === '' || value === false) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }).length;
  };

  const filtrosActivos = contarFiltrosActivos();

  // Renderizar filtro según su tipo
  const renderizarFiltro = (key, config) => {
    switch (config.tipo) {
      case 'boolean':
        return (
          <div key={key} className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={filtros[key] || false}
                onChange={(e) => actualizarFiltro(key, e.target.checked)}
                className="w-5 h-5 text-casal border-gray-300 rounded focus:ring-casal cursor-pointer"
              />
              <span className="ml-3 text-gray-700 dark:text-gray-300 font-medium">
                {config.label}
                {config.es_recomendado && (
                  <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                    Recomendado
                  </span>
                )}
              </span>
            </label>
            {config.descripcion && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-8">
                {config.descripcion}
              </p>
            )}
          </div>
        );

      case 'text':
        return (
          <div key={key} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {config.label}
              {config.es_recomendado && (
                <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                  Recomendado
                </span>
              )}
            </label>
            <input
              type="text"
              value={filtros[key] || ''}
              onChange={(e) => actualizarFiltro(key, e.target.value)}
              placeholder={config.placeholder || ''}
              className="w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
            />
            {config.descripcion && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {config.descripcion}
              </p>
            )}
          </div>
        );

      case 'select': {
        // Resolver opciones dinámicas
        let opciones = config.opciones || [];
        if (config.opciones_dinamicas === 'eventos' && eventos?.length > 0) {
          opciones = eventos.map(ev => ({ value: ev.id, label: ev.nombreEvento || ev.nombre_evento || ev.nombre || ev.id }));
        }
        return (
          <div key={key} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {config.label}
              {config.es_recomendado && (
                <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                  Recomendado
                </span>
              )}
            </label>
            <select
              value={filtros[key] || ''}
              onChange={(e) => actualizarFiltro(key, e.target.value)}
              className="w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 cursor-pointer"
            >
              <option value="">{config.placeholder || 'Seleccionar...'}</option>
              {opciones.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
            {config.descripcion && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {config.descripcion}
              </p>
            )}
          </div>
        );
      }

      case 'number':
        return (
          <div key={key} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {config.label}
              {config.es_recomendado && (
                <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                  Recomendado
                </span>
              )}
            </label>
            <div className="relative">
              {config.prefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {config.prefix}
                </span>
              )}
              <input
                type="number"
                value={filtros[key] || ''}
                onChange={(e) => actualizarFiltro(key, e.target.value ? parseInt(e.target.value) : null)}
                min={config.min}
                max={config.max}
                placeholder={config.placeholder || ''}
                className={clsx(
                  "w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300",
                  config.prefix && "pl-8"
                )}
              />
            </div>
            {config.descripcion && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {config.descripcion}
              </p>
            )}
          </div>
        );

      case 'tristate':
        return (
          <div key={key} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {config.label}
              {config.es_recomendado && (
                <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                  Recomendado
                </span>
              )}
            </label>
            <div className="flex gap-4">
              {config.opciones.map((opcion) => (
                <button
                  key={String(opcion.value)}
                  onClick={() => actualizarFiltro(key, opcion.value)}
                  className={clsx(
                    "flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all",
                    filtros[key] === opcion.value
                      ? "border-casal bg-casal text-white"
                      : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-casal/50"
                  )}
                >
                  {opcion.label}
                </button>
              ))}
            </div>
            {config.descripcion && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {config.descripcion}
              </p>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div key={key} className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {config.label}
              {config.es_recomendado && (
                <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                  Recomendado
                </span>
              )}
            </label>
            <div className="space-y-2">
              {config.opciones.map((opcion) => {
                const valor = typeof opcion === 'string' ? opcion : opcion.value;
                const etiqueta = typeof opcion === 'string' ? opcion : opcion.label;
                const estaSeleccionado = Array.isArray(filtros[key]) && filtros[key].includes(valor);

                return (
                  <label key={valor} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={estaSeleccionado}
                      onChange={(e) => {
                        const valoresActuales = Array.isArray(filtros[key]) ? filtros[key] : [];
                        const nuevosValores = e.target.checked
                          ? [...valoresActuales, valor]
                          : valoresActuales.filter((v) => v !== valor);
                        actualizarFiltro(key, nuevosValores);
                      }}
                      className="w-4 h-4 text-casal border-gray-300 rounded focus:ring-casal cursor-pointer"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      {etiqueta}
                    </span>
                  </label>
                );
              })}
            </div>
            {config.descripcion && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {config.descripcion}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Verificar si hay disparador configurado
  const hayDisparadorConfigado = disparador.tipo && 
    (disparador.tipo !== 'evento' || disparador.evento_tipo);

  return (
    <div>
      <h3 className="text-xl font-bold text-casal mb-4">
        2. Filtros de Destinatarios
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Define qué usuarios recibirán esta automatización. 
        {filtrosActivos > 0 && (
          <span className="ml-2 text-sm font-semibold text-casal">
            ({filtrosActivos} {filtrosActivos === 1 ? 'filtro activo' : 'filtros activos'})
          </span>
        )}
      </p>

      {/* Selector de tipo de destinatario */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          ¿A quién se enviará esta campaña?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { value: "invitados", label: "Invitados / Graduados", desc: "Personas registradas en el evento" },
            { value: "usuarios", label: "Usuarios del Sistema", desc: "Administradores y organizadores" },
            { value: "ambos", label: "Ambos", desc: "Envío simultáneo a invitados y usuarios" },
          ].map((tipo) => (
            <button
              key={tipo.value}
              onClick={() => setFiltros({ ...filtros, destinatarios_tipo: tipo.value })}
              className={clsx(
                "p-3 rounded-lg border-2 text-left transition-all",
                filtros.destinatarios_tipo === tipo.value
                  ? "border-casal bg-casal/10"
                  : "border-gray-300 dark:border-gray-600 hover:border-casal/50"
              )}
            >
              <div className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                {tipo.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {tipo.desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mensaje de ayuda contextual */}
      {mensajeAyuda && hayDisparadorConfigado && filtros.destinatarios_tipo !== "usuarios" && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex gap-3">
          <Info size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
              Filtros contextuales
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {mensajeAyuda}
            </p>
          </div>
        </div>
      )}

      {/* Advertencia si no hay disparador configurado */}
      {!hayDisparadorConfigado && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex gap-3">
          <AlertCircle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-1">
              Configura el disparador primero
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Los filtros disponibles dependen del tipo de disparador que selecciones. 
              Vuelve al paso 1 para configurar el disparador.
            </p>
          </div>
        </div>
      )}

      {/* Renderizar filtros relevantes */}
      {/* Filtros de Invitados */}
      {filtros.destinatarios_tipo !== "usuarios" && (
        <>
          {Object.keys(filtrosRelevantes).length > 0 ? (
            <div className="space-y-1">
              {Object.entries(filtrosRelevantes).map(([key, config]) =>
                renderizarFiltro(key, config)
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <p>Selecciona un tipo de disparador en el paso 1 para ver los filtros disponibles.</p>
            </div>
          )}
        </>
      )}

      {/* Filtros de Usuarios del Sistema */}
      {filtros.destinatarios_tipo !== "invitados" && (
        <div className={clsx(filtros.destinatarios_tipo === "ambos" && "mt-6 pt-6 border-t border-gray-200 dark:border-gray-700")}>
          {filtros.destinatarios_tipo === "ambos" && (
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wide">
              Filtros para Usuarios del Sistema
            </h4>
          )}
          <div className="space-y-1">
            {Object.entries(obtenerFiltrosUsuario()).map(([key, config]) =>
              renderizarFiltro(key, config)
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ========== PANEL 3: ACCIONES ==========
function PanelAcciones({ acciones, setAcciones, errores }) {
  const agregarAccion = (tipo) => {
    const nuevaAccion = {
      tipo,
      plantilla: "",
      mensaje: "",
      asunto: "",
      activa: true,
    };
    setAcciones([...acciones, nuevaAccion]);
  };

  const eliminarAccion = (index) => {
    setAcciones(acciones.filter((_, i) => i !== index));
  };

  const actualizarAccion = (index, campo, valor) => {
    const nuevasAcciones = [...acciones];
    nuevasAcciones[index] = { ...nuevasAcciones[index], [campo]: valor };
    setAcciones(nuevasAcciones);
  };

  return (
    <div>
      <h3 className="text-xl font-bold text-casal mb-4">3. Acciones a Ejecutar</h3>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Define qué acciones se ejecutarán cuando se dispare la automatización
      </p>

      {/* Lista de acciones */}
      <div className="space-y-4 mb-6">
        {acciones.map((accion, index) => (
          <div
            key={index}
            className="p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={accion.activa}
                  onChange={(e) =>
                    actualizarAccion(index, "activa", e.target.checked)
                  }
                  className="w-5 h-5 text-casal border-gray-300 rounded focus:ring-casal"
                />
                <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
                  {accion.tipo === "whatsapp" && "📱 WhatsApp"}
                  {accion.tipo === "email" && "📧 Email"}
                  {accion.tipo === "signalr" && "🔔 Notificación SignalR"}
                </span>
              </div>
              <Button
                onClick={() => eliminarAccion(index)}
                className="text-red-500 hover:text-red-700 font-medium"
              >
                Eliminar
              </Button>
            </div>

            {/* Configuración de WhatsApp */}
            {accion.tipo === "whatsapp" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Plantilla (Opcional)
                </label>
                <input
                  type="text"
                  value={accion.plantilla}
                  onChange={(e) =>
                    actualizarAccion(index, "plantilla", e.target.value)
                  }
                  placeholder="nombre_plantilla"
                  className="w-full p-2 mb-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
                />

                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Mensaje
                </label>
                <RichTextEditor
                  value={accion.mensaje}
                  onChange={(contenido) =>
                    actualizarAccion(index, "mensaje", contenido)
                  }
                  placeholder="Hola {{nombre}}, este es tu mensaje..."
                />
              </div>
            )}

            {/* Configuración de Email */}
            {accion.tipo === "email" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Asunto
                </label>
                <input
                  type="text"
                  value={accion.asunto}
                  onChange={(e) =>
                    actualizarAccion(index, "asunto", e.target.value)
                  }
                  placeholder="Asunto del correo"
                  className="w-full p-2 mb-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
                />

                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Plantilla
                </label>
                <input
                  type="text"
                  value={accion.plantilla}
                  onChange={(e) =>
                    actualizarAccion(index, "plantilla", e.target.value)
                  }
                  placeholder="nombre_plantilla_email"
                  className="w-full p-2 mb-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
                />

                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Contenido del Email
                </label>
                <RichTextEditor
                  value={accion.mensaje}
                  onChange={(contenido) =>
                    actualizarAccion(index, "mensaje", contenido)
                  }
                  placeholder="Escribe el contenido del correo aquí..."
                />
              </div>
            )}

            {/* Configuración de SignalR */}
            {accion.tipo === "signalr" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Target (Nombre del método)
                </label>
                <input
                  type="text"
                  value={accion.plantilla}
                  onChange={(e) =>
                    actualizarAccion(index, "plantilla", e.target.value)
                  }
                  placeholder="Ej: pagoCompletado"
                  className="w-full p-2 mb-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
                />

                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Destinatarios (IDs separados por coma)
                </label>
                <input
                  type="text"
                  value={accion.mensaje}
                  onChange={(e) =>
                    actualizarAccion(index, "mensaje", e.target.value)
                  }
                  placeholder="id1,id2,id3 o deja vacío para todos"
                  className="w-full p-2 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
                />
              </div>
            )}

            {errores[`accion_${index}`] && (
              <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
                {errores[`accion_${index}`]}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Botones para agregar acciones */}
      <div className="flex gap-3">
        <Button
          onClick={() => agregarAccion("whatsapp")}
          className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
        >
          + WhatsApp
        </Button>
        <Button
          onClick={() => agregarAccion("email")}
          className="flex-1 py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
        >
          + Email
        </Button>
        <Button
          onClick={() => agregarAccion("signalr")}
          className="flex-1 py-3 px-4 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
        >
          + SignalR
        </Button>
      </div>

      {errores.acciones && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
          {errores.acciones}
        </div>
      )}
    </div>
  );
}

// ========== PANEL 4: FRECUENCIA ==========
function PanelFrecuencia({ frecuencia, setFrecuencia, disparador, frecuenciaRecomendada, error }) {
  // Nombre legible del disparador para el banner
  const nombreDisparador = disparador?.evento_tipo
    ? disparador.evento_tipo.replace(/_/g, " ")
    : disparador?.tipo?.replace(/_/g, " ") || "";

  return (
    <div>
      <h3 className="text-xl font-bold text-casal mb-4">
        4. Frecuencia de Envío
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Define con qué frecuencia se puede enviar esta automatización a cada
        destinatario
      </p>

      {/* Banner de recomendación contextual */}
      {frecuenciaRecomendada && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
            💡 Recomendación para «{nombreDisparador}»
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
            {frecuenciaRecomendada.razon}
          </p>
          {frecuencia.tipo !== frecuenciaRecomendada.tipo && (
            <button
              onClick={() => setFrecuencia({ ...frecuencia, tipo: frecuenciaRecomendada.tipo })}
              className="text-xs font-semibold text-amber-800 dark:text-amber-300 underline hover:no-underline"
            >
              Aplicar recomendación ({frecuenciaRecomendada.tipo})
            </button>
          )}
        </div>
      )}

      {/* Tipo de frecuencia */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Tipo de frecuencia
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              value: "unica",
              label: "Única",
              desc: "Solo se envía una vez por destinatario",
            },
            {
              value: "recurrente",
              label: "Recurrente",
              desc: "Se puede enviar múltiples veces con intervalo",
            },
            {
              value: "continua",
              label: "Continua",
              desc: "Se envía cada vez que se cumple la condición",
            },
          ].map((tipo) => (
            <button
              key={tipo.value}
              onClick={() =>
                setFrecuencia({ ...frecuencia, tipo: tipo.value })
              }
              className={clsx(
                "p-4 rounded-lg border-2 text-left transition-all",
                frecuencia.tipo === tipo.value
                  ? "border-casal bg-casal/10"
                  : "border-gray-300 dark:border-gray-600 hover:border-casal/50"
              )}
            >
              <div className="font-semibold text-gray-800 dark:text-gray-200">
                {tipo.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {tipo.desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Configuración para recurrente */}
      {frecuencia.tipo === "recurrente" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Intervalo entre envíos (horas)
            </label>
            <input
              type="number"
              min="1"
              max="720"
              value={frecuencia.intervalo_horas}
              onChange={(e) =>
                setFrecuencia({
                  ...frecuencia,
                  intervalo_horas: parseInt(e.target.value),
                })
              }
              className="w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Tiempo mínimo entre envíos al mismo destinatario
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Máximo de envíos por destinatario
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={frecuencia.max_envios}
              onChange={(e) =>
                setFrecuencia({
                  ...frecuencia,
                  max_envios: parseInt(e.target.value),
                })
              }
              className="w-full p-3 border rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Número máximo de veces que se enviará al mismo destinatario
            </p>
          </div>
        </div>
      )}

      {/* Información adicional según tipo */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-800 dark:text-blue-300 font-semibold mb-2">
          {frecuencia.tipo === "unica" && "Frecuencia Única"}
          {frecuencia.tipo === "recurrente" && "Frecuencia Recurrente"}
          {frecuencia.tipo === "continua" && "Frecuencia Continua"}
        </p>
        <p className="text-xs text-blue-700 dark:text-blue-400">
          {frecuencia.tipo === "unica" &&
            "Esta automatización solo se ejecutará una vez por destinatario, sin importar cuántas veces se cumpla la condición."}
          {frecuencia.tipo === "recurrente" &&
            `Esta automatización se puede ejecutar hasta ${frecuencia.max_envios} veces por destinatario, con un intervalo mínimo de ${frecuencia.intervalo_horas} horas entre cada envío.`}
          {frecuencia.tipo === "continua" &&
            "Esta automatización se ejecutará cada vez que se cumpla la condición, sin límites de frecuencia. Úsala con precaución."}
        </p>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

